import React from "react";
import { LegalDocument } from "@/components/LegalDocument";
import { installerBranding } from "@/branding/Branding";
import { appConfig } from "@/config/app";

const sections = [
    { type: "p", text:
        `This Privacy Policy explains how ${installerBranding.name} ("we", "us", "our") ` +
        `collects, uses and protects information about you when you use the ` +
        `${appConfig.name} portal. We are the data controller for the personal ` +
        `data we hold about our customers and installer team members.`,
    },

    { type: "h", text: "1. Information we collect" },
    { type: "p", text: "We collect information you provide directly and information generated as you use the app:" },
    { type: "list", items: [
        "Account details — your name, email address, encrypted password and role (homeowner or installer).",
        "Project details — installation address, system specification, estimated completion date and progress.",
        "Documents — files you upload or that we generate on your behalf (quotes, contracts, certificates, photos).",
        "Communication — messages exchanged between you and our team through the in-app chat.",
        "Appointments — scheduled visits, engineer assignments and any notes you or we add.",
        "Technical data — browser type, operating system and app version to help us diagnose issues.",
    ]},

    { type: "h", text: "2. How we use your information" },
    { type: "p", text: "Your data is used to deliver and improve the service you have engaged us to provide, specifically to:" },
    { type: "list", items: [
        "Manage your solar installation project from enquiry to handover.",
        "Communicate with you about your project, appointments and documents.",
        "Generate certificates, quotes and handover packs.",
        "Provide customer support and respond to your questions.",
        "Meet our legal, regulatory and contractual obligations.",
    ]},

    { type: "h", text: "3. Legal basis for processing" },
    { type: "p", text:
        "Under UK GDPR, we process your personal data on the basis of the contract we have with you " +
        "(to provide your installation and aftercare), our legitimate business interests " +
        "(project administration and service improvement), and to comply with legal obligations " +
        "(such as MCS certification, DNO approval and electrical safety records)." },

    { type: "h", text: "4. Sharing your information" },
    { type: "p", text: "We do not sell your data. We may share it only with:" },
    { type: "list", items: [
        "Members of the installer team assigned to your project.",
        "The Distribution Network Operator (DNO) as required for grid connection.",
        "Certification bodies (e.g. MCS, NAPIT) as required for compliance.",
        "Regulatory or law-enforcement bodies where we are legally required to do so.",
    ]},

    { type: "h", text: "5. Data retention" },
    { type: "p", text:
        "We keep your project data for as long as necessary to provide our service and to meet " +
        "regulatory and warranty obligations (typically the lifetime of the installation plus " +
        "any applicable statutory retention period). Chat messages and appointment history are " +
        "retained alongside your project file." },

    { type: "h", text: "6. Your rights" },
    { type: "p", text: "You have the following rights over your personal data:" },
    { type: "list", items: [
        "Access — request a copy of the data we hold about you.",
        "Rectification — ask us to correct data that is inaccurate.",
        "Erasure — ask us to delete your account and personal data (see below).",
        "Restriction — ask us to limit how we use your data.",
        "Portability — receive your data in a machine-readable format.",
        "Objection — object to processing based on our legitimate interests.",
        "Complaint — lodge a complaint with the Information Commissioner's Office (ico.org.uk).",
    ]},

    { type: "h", text: "7. Deleting your account" },
    { type: "p", text:
        `You can permanently delete your ${appConfig.name} account at any time from the Profile ` +
        "screen. When you delete your account we immediately remove your account record and — for " +
        "homeowners — the associated project, documents, appointments and chat history from the app. " +
        "Certain records may be retained separately by us where required by law (for example " +
        "electrical certificates and warranty registrations)." },

    { type: "h", text: "8. Security" },
    { type: "p", text:
        "We protect your data with industry-standard measures including encrypted passwords, " +
        "authenticated API access, and secure storage of session tokens on your device. No system " +
        "is completely secure, so please choose a strong password and let us know immediately if " +
        "you suspect unauthorised access to your account." },

    { type: "h", text: "9. Changes to this policy" },
    { type: "p", text:
        "We may update this Privacy Policy from time to time. The effective date at the top of the " +
        "page indicates when it was last revised. Material changes will be highlighted in the app." },
];

export default function PrivacyPolicy() {
    return (
        <LegalDocument
            title="Privacy Policy"
            subtitle={`How ${installerBranding.name} handles your data in ${appConfig.name}.`}
            effectiveDate={appConfig.releaseDate}
            sections={sections}
            testid="legal-privacy-screen"
        />
    );
}
